import { Link } from "react-router-dom";
import {
  Sparkles,
  Rocket,
  Layers,
  FileStack,
  Calculator,
  CalendarRange,
  UserCheck,
  ArrowRight,
  CheckCircle2,
  BookOpen,
  Lightbulb,
  PlayCircle,
  HelpCircle,
  ChevronRight,
  Zap,
  Hash,
  Percent,
  Wallet,
  ShieldCheck,
  Variable,
  Code2,
  Plus,
  Eye,
  ArrowUpRight,
  Coffee,
  GraduationCap,
} from "lucide-react";

/* ============================================================
   PRIMITIVES (match the new design system)
============================================================ */

function Card({ className = "", children, padded = true }) {
  return (
    <div
      className={`relative bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-16px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.06),0_12px_36px_-16px_rgba(15,23,42,0.12)] transition-shadow overflow-hidden ${padded ? "p-5 md:p-6" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

function Pill({ tone = "slate", children, icon: Icon }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[11px] font-bold ${tones[tone]}`}>
      {Icon ? <Icon size={11} /> : null}
      {children}
    </span>
  );
}

function StepCard({ stepNumber, accent, icon: Icon, iconBg, eyebrow, title, summary, time, examples = [], openLabel, openTo, openColor, children }) {
  return (
    <div className="relative bg-white rounded-3xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-16px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_28px_-12px_rgba(15,23,42,0.16)] hover:-translate-y-0.5 transition-all duration-300 overflow-hidden group">
      {/* Top accent strip */}
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />

      {/* Step number watermark */}
      <div
        className="absolute -top-3 -right-2 text-[180px] font-extrabold leading-none select-none pointer-events-none"
        style={{ color: "rgba(15,23,42,0.03)", letterSpacing: "-12px" }}
      >
        {stepNumber}
      </div>

      <div className="relative p-6 md:p-7">
        <div className="flex items-start gap-4">
          {/* Step badge */}
          <div className="flex flex-col items-center shrink-0">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${iconBg} text-white flex items-center justify-center shadow-md group-hover:scale-110 group-hover:rotate-3 transition`}>
              <Icon size={22} />
            </div>
            <div className="mt-2 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
              Step {stepNumber}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">{eyebrow}</span>
              {time ? <Pill tone="slate" icon={Coffee}>~{time}</Pill> : null}
            </div>
            <h3 className="text-[19px] md:text-[20px] font-extrabold text-slate-900 leading-tight">{title}</h3>
            <p className="text-[13.5px] text-slate-600 mt-2 leading-relaxed">{summary}</p>

            {/* Custom rich content */}
            {children ? <div className="mt-4">{children}</div> : null}

            {/* Examples */}
            {examples.length ? (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {examples.map((ex) => (
                  <span key={ex} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-[11.5px] font-semibold text-slate-700">
                    <CheckCircle2 size={10} className="text-emerald-500" />
                    {ex}
                  </span>
                ))}
              </div>
            ) : null}

            {/* CTA */}
            {openTo ? (
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={openTo}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-gradient-to-r ${openColor || iconBg} shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.55)] hover:-translate-y-0.5 transition`}
                >
                  <PlayCircle size={14} />
                  {openLabel || "Open this section"}
                  <ArrowRight size={13} className="group-hover:translate-x-0.5 transition" />
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Snippet({ label, expression, hint, tone = "blue" }) {
  const tones = {
    blue: "from-blue-50 to-indigo-50 border-blue-200 text-blue-900",
    emerald: "from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900",
    amber: "from-amber-50 to-orange-50 border-amber-200 text-amber-900",
    violet: "from-violet-50 to-fuchsia-50 border-violet-200 text-violet-900",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-3 ${tones[tone]}`}>
      <p className="text-[10px] uppercase tracking-wider font-extrabold opacity-70">{label}</p>
      <p className="font-mono text-[12.5px] font-bold mt-0.5 break-words">{expression}</p>
      {hint ? <p className="text-[11px] opacity-80 mt-1.5 italic">{hint}</p> : null}
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="group bg-white rounded-2xl border border-slate-200 p-4 hover:border-slate-300 transition open:shadow-[0_4px_12px_rgba(15,23,42,0.06)]">
      <summary className="flex items-center gap-3 cursor-pointer list-none">
        <span className="shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-sm">
          <HelpCircle size={14} />
        </span>
        <span className="text-[13.5px] font-bold text-slate-900 flex-1">{q}</span>
        <ChevronRight size={16} className="text-slate-400 group-open:rotate-90 transition" />
      </summary>
      <div className="mt-3 pl-10 text-[13px] text-slate-600 leading-relaxed">{a}</div>
    </details>
  );
}

/* ============================================================
   MAIN
============================================================ */

export default function SalaryQuickStartGuide() {
  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
      <div className="max-w-[1180px] mx-auto p-4 md:p-7 space-y-7">
        {/* HERO */}
        <header
          className="relative rounded-3xl overflow-hidden shadow-[0_10px_40px_-20px_rgba(37,99,235,0.45)]"
          style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #4338CA 30%, #2563EB 65%, #06B6D4 100%)" }}
        >
          <div className="absolute -top-16 -right-12 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-12 w-72 h-72 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-amber-300 via-pink-300 to-emerald-300" />

          <div className="relative p-6 md:p-10">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0 flex-1 max-w-2xl">
                <div
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-white/15 border border-white/25 rounded-full px-3 py-1 backdrop-blur-sm"
                  style={{ color: "#FFFFFF" }}
                >
                  <Sparkles size={11} style={{ color: "#FCD34D" }} />
                  Salary Engine · Quick Start
                </div>
                <h1
                  className="text-[28px] md:text-[40px] font-extrabold mt-3 leading-[1.05] drop-shadow-sm"
                  style={{ color: "#FFFFFF" }}
                >
                  Welcome to Salary Engine
                </h1>
                <p
                  className="text-[14px] md:text-[16px] mt-3 max-w-xl leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.92)" }}
                >
                  Set up your organisation's pay structure in 5 simple steps — components, templates, formulas, working days, and employee assignment.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white/15 border border-white/25 backdrop-blur"
                    style={{ color: "#FFFFFF" }}
                  >
                    <Coffee size={12} /> ~15 minutes
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white/15 border border-white/25 backdrop-blur"
                    style={{ color: "#FFFFFF" }}
                  >
                    <GraduationCap size={12} /> No prior setup required
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold bg-emerald-400/15 border border-emerald-300/40 backdrop-blur"
                    style={{ color: "#FFFFFF" }}
                  >
                    <ShieldCheck size={12} style={{ color: "#A7F3D0" }} /> India-compliant
                  </span>
                </div>
              </div>

              <Link
                to="/salary-v2/components"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-sm font-bold shadow-[0_10px_30px_-12px_rgba(0,0,0,0.4)] hover:shadow-[0_12px_36px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition"
                style={{ color: "#1D4ED8" }}
              >
                <Rocket size={16} />
                Start with Step 1
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </header>

        {/* WHAT IS SALARY ENGINE */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow">
              <Lightbulb size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">What is this?</p>
              <h2 className="text-[20px] font-extrabold text-slate-900 mt-0.5">The Salary Engine in 60 seconds</h2>
              <p className="text-[13.5px] text-slate-600 mt-2 leading-relaxed">
                Salary Engine is where you define <b>how every payslip is calculated</b> in your organisation. You build it once, then payroll uses it every month — automatically figuring out Basic, HRA, allowances, deductions, statutory contributions (PF / ESI / PT / TDS), pro-rated days for joiners and leavers, and net pay.
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-3">
                  <p className="text-[11px] font-bold text-blue-900 inline-flex items-center gap-1"><Layers size={11}/> Components</p>
                  <p className="text-[11.5px] text-blue-800/80 mt-0.5">Earnings & deductions: Basic, HRA, PF, etc.</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-3">
                  <p className="text-[11px] font-bold text-emerald-900 inline-flex items-center gap-1"><FileStack size={11}/> Templates</p>
                  <p className="text-[11.5px] text-emerald-800/80 mt-0.5">Reusable bundles of components per band / grade.</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-amber-50 to-orange-50 p-3">
                  <p className="text-[11px] font-bold text-amber-900 inline-flex items-center gap-1"><Calculator size={11}/> Formulas</p>
                  <p className="text-[11.5px] text-amber-800/80 mt-0.5">Math that turns CTC into the right monthly numbers.</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* STEPS */}
        <section className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white flex items-center justify-center shadow-sm">
              <BookOpen size={18} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400">The path</p>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-tight">5 steps to your first payslip</h2>
            </div>
          </div>

          {/* STEP 1 — COMPONENTS */}
          <StepCard
            stepNumber="1"
            accent="from-blue-500 via-indigo-500 to-violet-600"
            icon={Layers}
            iconBg="from-blue-500 to-indigo-600"
            eyebrow="Foundation"
            title="Create your salary components"
            summary="A component is one line on the payslip — like Basic, HRA, Conveyance, PF, or Professional Tax. Pick a category (Earning / Benefit / Deduction / Statutory), give it a code, and decide if it's taxable."
            time="4 min"
            examples={["BASIC — Basic Salary", "HRA — House Rent Allowance", "SPL — Special Allowance", "PF — Provident Fund", "PT — Professional Tax"]}
            openTo="/salary-v2/components"
            openLabel="Open Components Hub"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <Snippet
                tone="blue"
                label="Earning"
                expression='code: "BASIC"  ·  category: earning  ·  taxable: yes'
                hint="The Basic salary that drives most other calculations."
              />
              <Snippet
                tone="emerald"
                label="Allowance"
                expression='code: "HRA"  ·  category: earning  ·  exemption: India §10(13A)'
                hint="HRA gets partial exemption based on actual rent paid."
              />
              <Snippet
                tone="amber"
                label="Statutory"
                expression='code: "PF"  ·  category: statutory  ·  rate: 12% capped at the EPF wage ceiling (₹25,000 from 17 Sep 2026)'
                hint="Auto-uses your org's PF Settings page."
              />
              <Snippet
                tone="violet"
                label="Deduction"
                expression='code: "LOAN"  ·  category: deduction  ·  manual amount per month'
                hint="Used for loan EMIs, advances, etc."
              />
            </div>
          </StepCard>

          {/* STEP 2 — TEMPLATES */}
          <StepCard
            stepNumber="2"
            accent="from-emerald-500 via-teal-500 to-cyan-600"
            icon={FileStack}
            iconBg="from-emerald-500 to-teal-600"
            eyebrow="Bundle"
            title="Create salary templates"
            summary="A template is a named bundle of components used together — usually one per pay band / grade (Intern, Junior, Senior, Director). Once defined, you reuse it for every employee in that band."
            time="3 min"
            examples={["Junior Engineer Pack", "Senior Manager Pack", "Field Sales Pack", "Hourly Contract Pack"]}
            openTo="/salary-v2/groups"
            openLabel="Open Templates / Groups"
            openColor="from-emerald-500 to-teal-600"
          >
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-emerald-50/40 p-4">
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-slate-500 mb-2">Example template: "Senior Engineer Pack"</p>
              <ul className="space-y-1.5 text-[12.5px]">
                <li className="flex items-center justify-between"><span className="font-semibold">Basic</span><span className="font-mono text-blue-700">40% of CTC</span></li>
                <li className="flex items-center justify-between"><span className="font-semibold">HRA</span><span className="font-mono text-blue-700">50% of Basic</span></li>
                <li className="flex items-center justify-between"><span className="font-semibold">Special Allowance</span><span className="font-mono text-blue-700">balance to gross</span></li>
                <li className="flex items-center justify-between"><span className="font-semibold">PF (employee)</span><span className="font-mono text-rose-700">12% of capped wage</span></li>
                <li className="flex items-center justify-between"><span className="font-semibold">PT</span><span className="font-mono text-rose-700">state slab</span></li>
              </ul>
            </div>
          </StepCard>

          {/* STEP 3 — FORMULAS */}
          <StepCard
            stepNumber="3"
            accent="from-amber-500 via-orange-500 to-rose-600"
            icon={Calculator}
            iconBg="from-amber-500 to-orange-600"
            eyebrow="Math"
            title="Write the formulas"
            summary="A formula tells the engine how to compute one component from others. You can use simple percentages, conditionals, the safe expression language, and even reusable Derived Variables."
            time="5 min"
            examples={["Percentage of Basic", "If/else for slabs", "min() / max() caps", "round()", "Reference any component by code"]}
            openTo="/salary-v2/formula-builder"
            openLabel="Open Formula Builder"
            openColor="from-amber-500 to-orange-600"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <Snippet
                tone="amber"
                label="Basic = 40% of CTC"
                expression="round(CTC * 0.40 / 12, 0)"
                hint="Monthly Basic from annual CTC."
              />
              <Snippet
                tone="amber"
                label="HRA = 50% of Basic"
                expression="round(BASIC * 0.50, 0)"
                hint="Metro band; use 40% for non-metro."
              />
              <Snippet
                tone="blue"
                label="Special Allowance"
                expression="GROSS - (BASIC + HRA + CONV)"
                hint="Balancing component — fills to monthly gross."
              />
              <Snippet
                tone="violet"
                label="PF (capped)"
                expression="min(BASIC, 25000) * 0.12"
                hint="Capped at the EPF wage ceiling (₹25,000 from 17 Sep 2026; ₹15,000 before)."
              />
            </div>
            <div className="mt-4 flex items-center gap-2 text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
              <Code2 size={14} className="text-blue-600 shrink-0" />
              <span>You can also create <b>Derived Variables</b> (e.g. <code className="font-mono bg-white px-1 py-0.5 rounded">PF_WAGE</code>) and reuse them across components.</span>
            </div>
          </StepCard>

          {/* STEP 4 — MONTH DAYS */}
          <StepCard
            stepNumber="4"
            accent="from-cyan-500 via-sky-500 to-blue-600"
            icon={CalendarRange}
            iconBg="from-cyan-500 to-sky-600"
            eyebrow="Calendar"
            title="Configure how month days are counted"
            summary="To pro-rate joiners, leavers, LOP days, and partial months — the engine needs to know what counts as a 'working day'. Pick the convention that matches your organisation."
            time="2 min"
            examples={["Calendar days (28/29/30/31)", "Fixed 30 days", "Working days only", "Working days − holidays"]}
            openTo="/salary-v2/statutory"
            openLabel="Open Statutory / Calendar Setup"
            openColor="from-cyan-500 to-sky-600"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-cyan-50 to-sky-50 p-3">
                <p className="text-[11px] font-bold text-cyan-900">Calendar days</p>
                <p className="font-mono text-[12.5px] mt-0.5 text-cyan-800">pay = monthly_gross × (worked / days_in_month)</p>
                <p className="text-[11px] text-cyan-800/80 mt-1.5 italic">Most common in India. Feb is 28/29, Apr is 30, Jan is 31.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-3">
                <p className="text-[11px] font-bold text-blue-900">Fixed 30 days</p>
                <p className="font-mono text-[12.5px] mt-0.5 text-blue-800">pay = monthly_gross × (worked / 30)</p>
                <p className="text-[11px] text-blue-800/80 mt-1.5 italic">Simpler, predictable — same daily wage every month.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-3">
                <p className="text-[11px] font-bold text-emerald-900">Working days only</p>
                <p className="font-mono text-[12.5px] mt-0.5 text-emerald-800">pay = monthly_gross × (worked / working_days)</p>
                <p className="text-[11px] text-emerald-800/80 mt-1.5 italic">Mon–Fri or Mon–Sat. Weekends are paid as part of gross.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-3">
                <p className="text-[11px] font-bold text-violet-900">Working days − holidays</p>
                <p className="font-mono text-[12.5px] mt-0.5 text-violet-800">paid_days = working − public_holidays + worked_holidays</p>
                <p className="text-[11px] text-violet-800/80 mt-1.5 italic">Honours gazetted holidays from the Holiday Calendar.</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-[12px] text-slate-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <Lightbulb size={14} className="text-amber-600 shrink-0" />
              <span><b>Tip:</b> Once you choose, the engine handles new joiners, exits, and LOP days automatically — you don't compute it by hand.</span>
            </div>
          </StepCard>

          {/* STEP 5 — ASSIGN */}
          <StepCard
            stepNumber="5"
            accent="from-violet-500 via-fuchsia-500 to-pink-600"
            icon={UserCheck}
            iconBg="from-violet-500 to-fuchsia-600"
            eyebrow="Go live"
            title="Assign packages to employees"
            summary="Pick an employee, choose the right template, enter their CTC and effective date. That's it — payroll will start using this package from the effective month onward."
            time="1 min per employee"
            examples={["One employee at a time", "Edit anytime", "History preserved", "CTC auto-fills from profile"]}
            openTo="/employeesalarystructure"
            openLabel="Open Assign Salary"
            openColor="from-violet-500 to-fuchsia-600"
          >
            <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50/60 via-fuchsia-50/40 to-pink-50/40 p-4">
              <div className="flex items-center gap-2 text-[12.5px] text-violet-900 font-semibold">
                <ArrowRight size={14} />
                Almost done — the very last step before running payroll for the first time.
              </div>
            </div>
          </StepCard>
        </section>

        {/* HELPFUL CONCEPTS */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white flex items-center justify-center shadow-sm">
              <Zap size={18} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400">Going deeper</p>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-tight">Power-user concepts</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Variable, color: "from-violet-500 to-purple-600", title: "Derived variables", text: "Reusable expressions like PF_WAGE or GROSS that any component can reference.", to: "/salary-v2/derived-variables" },
              { icon: Wallet, color: "from-emerald-500 to-teal-600", title: "Benefits & deductions", text: "One-time bonuses, loans, advances, recoveries — managed separately from monthly pay.", to: "/salary-v2/benefits-deductions" },
              { icon: Eye, color: "from-blue-500 to-indigo-600", title: "Simulator", text: "Test your template with a sample CTC before going live. See every line of the payslip.", to: "/salary-v2/simulator" },
              { icon: ShieldCheck, color: "from-rose-500 to-pink-600", title: "Statutory (PF / ESI / PT / TDS)", text: "Centralised rate and slab configuration — the engine pulls from here automatically.", to: "/salary-v2/statutory" },
              { icon: Hash, color: "from-amber-500 to-orange-600", title: "Component dependencies", text: "Visualise which components depend on which — perfect for debugging tricky formulas.", to: "/salary-v2/dependencies" },
              { icon: Percent, color: "from-cyan-500 to-sky-600", title: "Audit trail", text: "Every change is logged with who changed what and when — for compliance and rollback.", to: "/salary-v2/audit" },
            ].map((c) => (
              <Link
                key={c.title}
                to={c.to}
                className="group bg-white rounded-2xl border border-slate-200/80 p-5 hover:border-slate-300 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${c.color} text-white flex items-center justify-center shadow-sm group-hover:scale-110 transition`}>
                    <c.icon size={16} />
                  </div>
                  <ArrowUpRight size={14} className="text-slate-300 group-hover:text-slate-600 transition" />
                </div>
                <p className="text-[13px] font-bold text-slate-900">{c.title}</p>
                <p className="text-[11.5px] text-slate-500 mt-1 leading-relaxed">{c.text}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center shadow-sm">
              <HelpCircle size={18} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400">Common questions</p>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-tight">Frequently asked</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FaqItem
              q="Do I need to re-create components for every employee?"
              a="No — components are defined once at the organisation level. You then bundle them into templates, and assign templates to employees. Change a formula once and every employee on that template recalculates."
            />
            <FaqItem
              q="What if I just want a fixed monthly salary with no splits?"
              a="Create a single 'BASIC' earning component equal to monthly gross, and skip the rest. The engine still handles statutory deductions if you turn them on."
            />
            <FaqItem
              q="How does the engine handle joiners and leavers mid-month?"
              a="Automatically — based on your chosen Month Days convention (Step 4). The engine pro-rates each earning by (worked_days ÷ month_days)."
            />
            <FaqItem
              q="Can I test a template before assigning it?"
              a="Yes. Open the Simulator with a sample CTC — it shows every line of the resulting payslip without saving anything to a real employee."
            />
            <FaqItem
              q="What about LOP (Loss of Pay) days?"
              a="LOP days are picked up from the Attendance module. Each LOP day reduces paid_days by 1 in the formula, so all earnings drop proportionally."
            />
            <FaqItem
              q="Where do PF / ESI / PT / TDS rates come from?"
              a="From the Statutory page (one place per org). When the law changes, you update it once and every payroll run uses the new value — no template edits needed."
            />
          </div>
        </section>

        {/* NEXT STEP CTA */}
        <Card className="overflow-hidden">
          <div
            className="absolute inset-0 opacity-90 pointer-events-none"
            style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #4338CA 100%)" }}
          />
          <div className="relative flex flex-wrap items-center justify-between gap-4 py-3">
            <div>
              <div
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-white/15 border border-white/25 rounded-full px-3 py-1 backdrop-blur"
                style={{ color: "#FFFFFF" }}
              >
                <Rocket size={11} style={{ color: "#FCD34D" }} />
                Ready to start
              </div>
              <h3
                className="text-[20px] md:text-[22px] font-extrabold mt-2 drop-shadow-sm"
                style={{ color: "#FFFFFF" }}
              >
                Let's build your first salary template
              </h3>
              <p
                className="text-[13px] mt-1 max-w-lg"
                style={{ color: "rgba(255,255,255,0.92)" }}
              >
                Step 1 takes about 4 minutes — by the end you'll have your first set of components ready to bundle.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/salary-v2/components"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-sm font-bold shadow-[0_10px_30px_-12px_rgba(0,0,0,0.4)] hover:shadow-[0_12px_36px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition"
                style={{ color: "#1D4ED8" }}
              >
                <Plus size={15} />
                Create your first component
              </Link>
              <Link
                to="/salary-v2/simulator"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/15 border border-white/30 text-sm font-semibold backdrop-blur-sm hover:bg-white/25 transition"
                style={{ color: "#FFFFFF" }}
              >
                <Eye size={14} />
                Or play in the Simulator
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
